import { useCallback, useEffect, useRef, useState } from 'react';
import Axios from '../api/axios';
import * as glb from '../globals';
import { subscribeNotifications } from '../services/notificationService';
import { getNotificationSocketState, subscribeNotificationSocket } from '../utils/notificationSocket';
import { publishBandCount } from '../utils/browserBandNotification';

const FALLBACK_POLL_MS = 60_000;
const EMPTY_OZET = { ilgi_sayisi: 0, bilgi_sayisi: 0, arama_sayisi: 0, genel_yetkili: false };

function normalizeOzet(data) {
  return {
    ilgi_sayisi: Number(data?.ilgi_sayisi) || 0,
    bilgi_sayisi: Number(data?.bilgi_sayisi) || 0,
    arama_sayisi: Number(data?.arama_sayisi) || 0,
    genel_yetkili: !!data?.genel_yetkili,
  };
}

export function useCariIlgiBildirim() {
  const [ozet, setOzet] = useState(EMPTY_OZET);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const fetchOzet = useCallback(async () => {
    const kulid = glb.default?.KULLANICI?.KUL_REF;
    if (!kulid) return EMPTY_OZET;
    try {
      setLoading(true);
      const res = await Axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER}cari_ilgi_bildirim/ozet`,
      );
      const next = normalizeOzet(res.data || EMPTY_OZET);
      if (mountedRef.current) setOzet(next);
      publishBandCount('ilgi', next.ilgi_sayisi, {
        title: 'İlgi',
        body: next.ilgi_sayisi === 1
          ? '1 müşteri arama veya bilgi istiyor'
          : `${next.ilgi_sayisi} müşteri arama veya bilgi istiyor`,
      });
      return next;
    } catch (_) {
      return EMPTY_OZET;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const applyOzet = useCallback((data) => {
    if (!mountedRef.current || !data) return;
    const next = normalizeOzet(data);
    setOzet(next);
    publishBandCount('ilgi', next.ilgi_sayisi, {
      title: 'İlgi',
      body: next.ilgi_sayisi === 1
        ? '1 müşteri arama veya bilgi istiyor'
        : `${next.ilgi_sayisi} müşteri arama veya bilgi istiyor`,
    });
  }, []);

  const markOkundu = useCallback(async (carRefs) => {
    const kulid = glb.default?.KULLANICI?.KUL_REF;
    if (!kulid || !Array.isArray(carRefs) || !carRefs.length) return;
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER}cari_ilgi_bildirim/okundu`,
        { kulid, car_refs: carRefs },
      );
      applyOzet(res.data || EMPTY_OZET);
    } catch (_) { /* ignore */ }
  }, [applyOzet]);

  useEffect(() => {
    mountedRef.current = true;
    fetchOzet();

    const onNotification = (n) => {
      const type = String(n?.type || '').toUpperCase();
      if (type === 'CARI_ILGI_BADGE' && n?.data?.ozet) {
        applyOzet(n.data.ozet);
        return;
      }
      if (type === 'CARI_ILGI_BADGE') {
        fetchOzet();
      }
    };

    const unsub = subscribeNotifications(onNotification);
    const unsubTransport = subscribeNotificationSocket((msg) => {
      if (msg?.type === 'WS_OPEN') fetchOzet();
    });

    const onWsEvent = (evt) => onNotification(evt?.detail || {});
    window.addEventListener('cari-ilgi-bildirim-ws', onWsEvent);

    const timer = setInterval(fetchOzet, FALLBACK_POLL_MS);

    const onFocus = () => {
      if (getNotificationSocketState() !== 'open') fetchOzet();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchOzet();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('cari-ilgi-bildirim-refresh', fetchOzet);

    return () => {
      mountedRef.current = false;
      unsub();
      unsubTransport();
      window.removeEventListener('cari-ilgi-bildirim-ws', onWsEvent);
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('cari-ilgi-bildirim-refresh', fetchOzet);
    };
  }, [fetchOzet, applyOzet]);

  return { ozet, loading, refresh: fetchOzet, markOkundu };
}
